import React from 'react';
import {
    polygonsBorderingOptions,
    polygonsShowLiteOptions,
    polygonsShowOptions
} from '../../helpers/StyleConstants';
import {MapContainer, Marker, Polygon, Popup, TileLayer, useMap} from "react-leaflet";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import "./Leaflet.scss";
import mainIcon from '../../img/markers/marker-icon.png';
import secondIcon from '../../img/markers/marker-icon-partner.svg';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import IMapConfig from "../../interfaces/IMapConfig";
import IPolygon from "../../interfaces/IPolygon";
import IPartner from "../../interfaces/IPartner";
import TownAPIs from "../../APIs/TownAPIs";
import Spinner from "../spinner/Spinner";

const MapView: React.FC<{ position: L.LatLngExpression, zoom?: number, mapConfig: IMapConfig }> = props => {
    const map = useMap();

    map.setView(props.position, props.zoom ?? map.getZoom());

    props.mapConfig.canISetView = false;

    return null;
};

const MapPositionMarker: React.FC<{ position: L.LatLngExpression }> = props => {
    const icon = L.icon({
        iconUrl: mainIcon,
        shadowUrl: iconShadow,
        iconSize: [25, 41],
        iconAnchor: [10, 41]
    });

    return (
        <Marker position={props.position} icon={icon}/>
    );
};


const MapPartnersLocationsMarkers: React.FC<{ mapConfig: IMapConfig }> = props => {
    const icon = L.icon({
        iconUrl: secondIcon,
        iconSize: [32, 41],
        iconAnchor: [10, 41]
    });

    const partnersLocations: Map<string, any> | undefined = props.mapConfig.partnersLocations;

    if (partnersLocations && 0 < partnersLocations.size) {
        const locations: any = Array.from(partnersLocations.values());

        return locations.map((values: any) => {
                return values.map((partnerLocation: any) => {
                        const position: L.LatLngExpression = [partnerLocation.latitude, partnerLocation.longitude];

                        return (
                            <Marker key={partnerLocation.id} position={position} icon={icon}>
                                <Popup>{partnerLocation.nom} <br/> {partnerLocation.adresse}</Popup>
                            </Marker>
                        )
                    }
                )
            }
        );
    }

    return null;
};

interface MapPolygonsProps {
    mapConfig: IMapConfig,
    setMapConfig: Function,
    partners: IPartner[],
    setPartners: Function
}

const MapPolygons: React.FC<MapPolygonsProps> = (props: MapPolygonsProps) => {
    const map = useMap();
    const { polygons } = props.mapConfig;

    const polygonsFc = getPolygonsComponents(polygons, props.mapConfig, props.setMapConfig, map, props.partners, props.setPartners);

    if (null !== polygonsFc) {
        return polygonsFc.map((element: any) => element);
    }

    return null;
};

const getPolygonsComponents = (polygons: IPolygon[], mapConfig: IMapConfig, setMapConfig: Function, map: L.Map, partners: IPartner[], setPartners: Function): any => {
    if (null === polygons || 0 === polygons.length) {
        return null;
    }

    const eventHandlers:any = (index: number) => {
        const polygon = mapConfig.polygons[index];
        const updateMapZoomAndPolygonOption  = (options: any) => onMouseHandler(index, polygon, mapConfig, setMapConfig, map, options);

        return {
            click: () => {
                onClickHandler(index, polygon, mapConfig, setMapConfig, map);

                interkabPartnersUpdater(polygon, partners, setPartners);

                interkabLocationsUpdater(polygon, mapConfig, setMapConfig);
            },
            mouseover: () => {
                updateMapZoomAndPolygonOption(polygonsShowLiteOptions);
            },
            mouseout: () => {
                updateMapZoomAndPolygonOption(polygonsBorderingOptions);
            },
        }
    };

    return polygons.map((polygon: IPolygon, index: number) => {
        return <Polygon key={index}
                        positions={polygon.positions}
                        pathOptions={polygon.options}
                        eventHandlers={eventHandlers(index)}
        />;
    });
};

const onClickHandler = (index: number, polygon: IPolygon, mapConfig: IMapConfig, setMapConfig: Function, map: L.Map) => {
    const oldOptions: any = polygon.options;
    const condition: boolean = 0 === oldOptions.opacity || !polygon.isSelected;

    const mapConfigUpd = {...mapConfig};
    mapConfigUpd.zoom = map.getZoom();

    const polygonUpd = mapConfigUpd.polygons[index];
    polygonUpd.options = condition ? polygonsShowOptions: polygonsBorderingOptions;
    polygonUpd.onMouseHandler = !condition;
    polygonUpd.isSelected = condition;

    mapConfigUpd.polygons[index] = polygonUpd;

    setMapConfig({...mapConfigUpd});
};

const onMouseHandler = (index: number, polygon: IPolygon, mapConfig: IMapConfig, setMapConfig: Function, map: L.Map, options: {color: string, opacity: number, fillOpacity: number}) => {
    if (polygon.isSelected || polygon.onMouseHandler === false) {
        return null;
    }

    const mapConfigUpd = {...mapConfig};
    mapConfigUpd.zoom = map.getZoom();
    mapConfigUpd.polygons[index].options = options;

    setMapConfig({...mapConfigUpd});
};

const interkabPartnersUpdater = (polygon: IPolygon, partners: IPartner[], setPartners: Function) => {
    const currentPartners = [...partners];
    const partnerExists = partners.some(p => p.townId === polygon.townId && p.insee === polygon.insee);

    if (partnerExists) {
        const postalCodes : string[] = polygon.postalCodes;
        const indexes = postalCodes.map((postalCode : string) => {
            return partners.findIndex(p => p.townId === polygon.townId && p.insee === polygon.insee && p.postalCode === postalCode);
        })

        const updPartners = currentPartners.filter((_, index) => !indexes.includes(index));
        setPartners(updPartners);
    }

    if (!partnerExists) {
        const postalCodes = polygon.postalCodes;
        const names = Array(postalCodes.length).fill(polygon.normalizedName);
        const query = { names: names, postalCodes: postalCodes };

        TownAPIs.getInterkabPartnersInMultipleTowns(query).then((response: any[] | null) => {
           if(response) {
               const newPartners : IPartner[] = response.map(r => {
                    return ({
                        townId: polygon.townId,
                        insee: polygon.insee,
                        name: polygon.name,
                        postalCode: r.code,
                        nbAgencies: Number(r.nbagences),
                    })
               });

               setPartners([...currentPartners, ...newPartners]);
           }
        });
    }
}

const interkabLocationsUpdater = (polygon: IPolygon, mapConfig: IMapConfig, setMapConfig: Function) => {
    const partnersLocations = mapConfig.partnersLocations ?? new Map<string, any>();
    const postalCodes = polygon.postalCodes;
    const keys = postalCodes.map(postalCode => [polygon.normalizedName, postalCode].join(','));

    const actualKeys = keys.filter(k => partnersLocations.has(k));
    const somePartnersExist = actualKeys.length > 0;

    if (somePartnersExist) {
        actualKeys.forEach(k => partnersLocations.delete(k));

        setMapConfig((mapConfig : IMapConfig) => {
            return {
                ...mapConfig,
                partnersLocations: partnersLocations
            }
        })
    }

    if (!somePartnersExist) {
        const postalCodes = polygon.postalCodes;
        const names = Array(postalCodes.length).fill(polygon.normalizedName);
        const query = { names: names, postalCodes: postalCodes };

        TownAPIs.getInterkabPartnersLocationsInMultipleTown(query).then((partnersResponse : any[]) => {
            if(partnersResponse) {
                partnersResponse.forEach(partner => {
                    const key = [partner.ville, partner.code];
                    partnersLocations.set(key.join(','), partner.agences)
                })
            }

            setMapConfig((mapConfig : IMapConfig) => {
                return {
                    ...mapConfig,
                    partnersLocations: partnersLocations
                }
            })
        })
    }
}

const Leaflet: React.FC<{ mapConfig: IMapConfig, setMapConfig: Function, partners: IPartner[], setPartners: Function, loader: boolean, lfRef: any}> = props => {
    const mapUrl: string = `${process.env.REACT_APP_IKG_MAP}?access-token=${process.env.REACT_APP_IKG_JAWG_MAP_ACCESS_TOKEN}`;
    const position = props.mapConfig.position;
    const zoom = props.mapConfig.zoom ?? 10;
    const minZoom = zoom - 4;
    const maxZoom = zoom + 3;
    const swz = props.mapConfig.swz ?? false;
    const dragging = props.mapConfig.dragging ?? false;
    const zoomControl = props.mapConfig.zoomControl ?? true;

    return (
        (null !== position && undefined !== position) ?
            <>
                <div ref={props.lfRef} className="leafletBigContainer">
                    <MapContainer id="leafletMap" center={position} zoom={zoom} minZoom={minZoom} maxZoom={maxZoom} scrollWheelZoom={swz} dragging={dragging} zoomControl={zoomControl}>
                        <TileLayer attribution='&copy; IK Generator Map' url={mapUrl}/>
                        {
                            props.mapConfig.canISetView
                            && <MapView position={position} zoom={zoom} mapConfig={props.mapConfig}/>
                        }
                        {
                            props.mapConfig.showPartnersLoc
                            && <MapPartnersLocationsMarkers mapConfig={props.mapConfig}/>
                        }
                        <MapPositionMarker position={position}/>
                        <MapPolygons mapConfig={props.mapConfig} setMapConfig={props.setMapConfig} partners={props.partners} setPartners={props.setPartners}/>
                    </MapContainer>
                </div>
                {
                    props.loader && <Spinner propClass="spinner absolute active"/>
                }
            </>
            :
            <Spinner/>
    );
};

export default Leaflet;
